import React, { useEffect, useState } from 'react';
import { Drawer, Button, Form, Input, InputNumber, DatePicker, Switch, Typography, Alert, Divider, Select, Radio, message } from 'antd';
import ApiService from '../../APIServices/ApiService';
import COUNTRIES from '../../Utils/countries';
import dayjs from 'dayjs';

/**
 * The one place a discount is configured. There is no separate "Discounts"
 * feature any more — it was an auto-applied price cut with no code, invisible to
 * customers on youtra.com, and it duplicated this maths. Everything it could
 * express is a coupon now.
 *
 * The three scope fields (product types / categories / countries) all mean
 * "no restriction" when left empty, and they AND together: product types
 * [travel guide] + country [Pakistan] is a coupon for Pakistani travel guides only.
 */

const PRODUCT_TYPES = [
    { value: 'book', label: 'Travel guides' },
    { value: 'language_guide', label: 'Language guides' },
    { value: 'esim', label: 'eSIMs' },
    // The sister storefronts. They must be named EXPLICITLY — unlike every other
    // scope here, an empty Product Types does NOT include them. Those four are
    // separate businesses, so a YouGuide code left unscoped must not become
    // spendable on all of them; the shop refuses any coupon that does not say
    // `other_book`. Without this option no coupon could be used there at all.
    { value: 'other_book', label: 'Other Guides (YouFlora, Cybellium, …)' },
];

/**
 * Which sister storefronts an Other Guides coupon works on. Empty = all four.
 * Loaded from the API so a brand added to the backend registry appears here
 * without a frontend release.
 */
const FALLBACK_SITES = [
    { value: 'youflora', label: 'YouFlora' },
    { value: 'cybellium', label: 'Cybellium' },
    { value: 'accounvia', label: 'Accounvia' },
    { value: 'lawlegiance', label: 'Lawlegiance' },
];

const CouponPopup = ({ type, coupon, open, setOpen, onSaveCoupon }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [sites, setSites] = useState(FALLBACK_SITES);
    const discountType = Form.useWatch('discountType', form) || 'percentage';
    const productTypes = Form.useWatch('productTypes', form) || [];

    // Categories only exist on travel guides, so the picker is pointless — and
    // actively misleading — when the coupon is scoped away from them.
    const categoriesApply = productTypes.length === 0 || productTypes.includes('book');
    // The store picker only means anything once Other Guides is in scope.
    const sitesApply = productTypes.includes('other_book');
    // Language guides are language pairs with no destination, so they can never
    // satisfy a country restriction. Say so rather than letting an admin build a
    // coupon that silently never fires.
    const countryWarning = productTypes.length === 1 && productTypes[0] === 'language_guide';

    useEffect(() => {
        if (!open) return;
        ApiService.getAllCategories()
            .then((res) => setCategories(Array.isArray(res) ? res : (res?.categories || [])))
            .catch(() => setCategories([]));
        // Soft-fails to the four known brands: an admin must still be able to
        // scope a coupon when the sites endpoint is briefly unavailable.
        // Returns the envelope `{ success, sites: [...] }`, not a bare array.
        ApiService.getOtherBookSites?.()
            .then((res) => {
                const list = Array.isArray(res) ? res : (res?.sites || []);
                const options = list
                    .map((s) => ({ value: s.key, label: s.name || s.key }))
                    .filter((o) => o.value);
                if (options.length) setSites(options);
            })
            .catch(() => { });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (type === 'Edit' && coupon) {
            form.setFieldsValue({
                code: coupon.code,
                title: coupon.title || '',
                discountType: coupon.discountType || 'percentage',
                // Older coupons predate `value` and only carry `percentage`.
                value: coupon.value ?? coupon.percentage,
                minOrderAmount: coupon.minOrderAmount ?? null,
                maxDiscountAmount: coupon.maxDiscountAmount ?? null,
                maxItems: coupon.maxItems ?? null,
                productTypes: coupon.productTypes || [],
                categories: (coupon.categories || []).map((c) => c?._id || c),
                countries: coupon.countries || [],
                sites: coupon.sites || [],
                startDate: coupon.startDate ? dayjs(coupon.startDate) : null,
                expiryDate: coupon.expiryDate ? dayjs(coupon.expiryDate) : null,
                isActive: coupon.isActive,
                // Coupons created before this option existed have no field; they were
                // all sign-in-only, so absent must read as true, not false.
                loginRequired: coupon.loginRequired !== false,
                usageLimit: coupon.usageLimit || null,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({ isActive: true, loginRequired: true, discountType: 'percentage', productTypes: [], categories: [], countries: [], sites: [] });
        }
    }, [open]);

    const closeDrawer = () => setOpen(false);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const data = {
                code: values.code,
                title: values.title || '',
                discountType: values.discountType || 'percentage',
                value: values.value,
                minOrderAmount: values.minOrderAmount ?? null,
                maxDiscountAmount: values.discountType === 'fixed' ? null : (values.maxDiscountAmount ?? null),
                maxItems: values.maxItems ?? null,
                productTypes: values.productTypes || [],
                // Drop categories the admin picked and then scoped away from, so the
                // saved coupon matches what the form is now showing them.
                categories: categoriesApply ? (values.categories || []) : [],
                countries: values.countries || [],
                // Dropped when Other Guides is no longer in scope, for the same
                // reason categories are: save what the form is showing.
                sites: sitesApply ? (values.sites || []) : [],
                startDate: values.startDate ? values.startDate.toISOString() : null,
                expiryDate: values.expiryDate.toISOString(),
                isActive: values.isActive,
                loginRequired: values.loginRequired !== false,
                usageLimit: values.usageLimit || null,
            };

            const response = type === 'Edit'
                ? await ApiService.updateCoupon(coupon._id, data)
                : await ApiService.createCoupon(data);
            message.success(`Coupon ${type === 'Edit' ? 'updated' : 'created'} successfully.`);
            onSaveCoupon(response);
        } catch (error) {
            message.error(error?.response?.data?.message || 'Failed to save coupon.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer
            title={<Typography.Title level={3} className="fw-500">{type === 'Edit' ? 'Edit Coupon' : 'Add Coupon'}</Typography.Title>}
            width={520}
            onClose={closeDrawer}
            open={open}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={closeDrawer} style={{ marginRight: 8 }}>Cancel</Button>
                    <Button onClick={() => form.submit()} type="primary" loading={loading}>Submit</Button>
                </div>
            }
            bodyStyle={{ paddingBottom: 80 }}
        >
            <Alert
                message="Leave a scope field empty to apply the coupon everywhere. Scope fields combine: e.g. Travel guides + Pakistan means Pakistani travel guides only."
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
            />

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Divider>
                    <Typography.Text strong style={{ fontSize: '16px', color: '#1890ff' }}>Coupon Details</Typography.Text>
                </Divider>

                <Form.Item name="code" label="Coupon Code" rules={[{ required: true, message: 'Coupon code is required' }]}>
                    <Input placeholder="e.g. YOUSUM26" style={{ textTransform: 'uppercase' }} />
                </Form.Item>

                <Form.Item name="title" label="Title (shown to the customer in the cart)">
                    <Input placeholder="e.g. Summer Holiday Offer" />
                </Form.Item>

                <Form.Item name="discountType" label="Discount Type" initialValue="percentage">
                    <Radio.Group>
                        <Radio.Button value="percentage">Percentage (%)</Radio.Button>
                        <Radio.Button value="fixed">Fixed amount (€)</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                <Form.Item
                    name="value"
                    label={discountType === 'fixed' ? 'Discount Amount' : 'Discount Percentage'}
                    rules={[{ required: true, message: 'Discount value is required' }]}
                >
                    <InputNumber
                        min={discountType === 'fixed' ? 0.01 : 1}
                        max={discountType === 'fixed' ? undefined : 100}
                        placeholder={discountType === 'fixed' ? 'e.g. 5' : 'e.g. 10'}
                        style={{ width: '100%' }}
                        addonAfter={discountType === 'fixed' ? '€' : '%'}
                    />
                </Form.Item>

                <Form.Item
                    name="minOrderAmount"
                    label="Minimum Spend (optional)"
                    tooltip="Checked against the items this coupon applies to, not the whole cart. Leave empty for no minimum."
                >
                    <InputNumber min={0} placeholder="e.g. 10 — for '10% off above €10'" style={{ width: '100%' }} addonAfter="€" />
                </Form.Item>

                {discountType === 'percentage' && (
                    <Form.Item name="maxDiscountAmount" label="Maximum Discount (optional)" tooltip="Caps how much this coupon can ever take off.">
                        <InputNumber min={0} placeholder="Leave empty for no cap" style={{ width: '100%' }} addonAfter="€" />
                    </Form.Item>
                )}

                <Form.Item
                    name="maxItems"
                    label="Apply to how many items? (optional)"
                    tooltip="Set to 1 with 100% off to give away exactly one product. The discount goes to the most expensive matching item. Leave empty to apply to every matching item."
                >
                    <InputNumber min={1} placeholder="Leave empty for all matching items" style={{ width: '100%' }} />
                </Form.Item>

                <Divider>
                    <Typography.Text strong style={{ fontSize: '16px', color: '#1890ff' }}>What it applies to</Typography.Text>
                </Divider>

                <Form.Item name="productTypes" label="Product Types" tooltip="Empty = all products.">
                    <Select mode="multiple" allowClear placeholder="All products" options={PRODUCT_TYPES} />
                </Form.Item>

                {categoriesApply && (
                    <Form.Item name="categories" label="Travel Guide Categories" tooltip="Empty = all categories. Only travel guides have categories.">
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="All categories"
                            optionFilterProp="label"
                            options={categories.map((c) => ({ value: c._id, label: c.name }))}
                        />
                    </Form.Item>
                )}

                {sitesApply && (
                    <Form.Item
                        name="sites"
                        label="Which stores?"
                        tooltip="Empty = all Other Guides stores. Each one is a separate business, so scope a code to the store that is paying for the promotion."
                    >
                        <Select mode="multiple" allowClear placeholder="All Other Guides stores" options={sites} />
                    </Form.Item>
                )}

                <Form.Item name="countries" label="Countries" tooltip="Empty = worldwide. Matches a travel guide's country and an eSIM's destination.">
                    <Select mode="multiple" allowClear showSearch placeholder="Worldwide" optionFilterProp="label"
                        options={COUNTRIES.map((c) => ({ value: c, label: c }))} />
                </Form.Item>

                {countryWarning && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Language guides have no country, so adding countries above will stop this coupon from ever applying."
                    />
                )}

                <Divider>
                    <Typography.Text strong style={{ fontSize: '16px', color: '#1890ff' }}>Validity</Typography.Text>
                </Divider>

                <Form.Item name="startDate" label="Start Date (optional)" tooltip="Leave empty to make it live immediately.">
                    <DatePicker style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="expiryDate" label="Expiry Date" rules={[{ required: true, message: 'Expiry date is required' }]}>
                    <DatePicker style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="usageLimit" label="Usage Limit (optional)">
                    <InputNumber min={1} placeholder="Leave empty for unlimited" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    name="loginRequired"
                    label="Login Required"
                    valuePropName="checked"
                    tooltip="On: the shopper must sign in to apply this code, and each account can use it only once. Off: anyone with the code can apply it without an account, limited only by the Usage Limit above."
                >
                    <Switch />
                </Form.Item>

                <Form.Item name="isActive" label="Active" valuePropName="checked">
                    <Switch />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default CouponPopup;
